import type { GameId, StampKind } from './types';

const STORAGE_KEY = 'casper-arcade-save-v1';
const GAME_IDS: GameId[] = ['mossfire', 'drift-courier', 'dinky-diner', 'hexhold', 'twenty-one-terrors', 'orbital-salvage', 'echo-shift'];
const STAMP_IDS: StampKind[] = ['clear', 'skill', 'mastery'];

export interface ArcadeSave {
  version: 1;
  lastPlayed: GameId | null;
  playCounts: Partial<Record<GameId, number>>;
  highScores: Partial<Record<GameId, number>>;
  stamps: Partial<Record<GameId, StampKind[]>>;
  settings: {
    muted: boolean;
    reducedMotion: boolean;
  };
}

const freshSave = (): ArcadeSave => ({
  version: 1,
  lastPlayed: null,
  playCounts: {},
  highScores: {},
  stamps: {},
  settings: {
    muted: false,
    reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  },
});

export function loadSave(): ArcadeSave {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '') as unknown;
    if (!parsed || typeof parsed !== 'object' || (parsed as { version?: unknown }).version !== 1) return freshSave();
    const candidate = parsed as Partial<ArcadeSave>;
    const playCounts: ArcadeSave['playCounts'] = {};
    const highScores: ArcadeSave['highScores'] = {};
    const stamps: ArcadeSave['stamps'] = {};
    const countSource = candidate.playCounts && typeof candidate.playCounts === 'object' ? candidate.playCounts : {};
    const scoreSource = candidate.highScores && typeof candidate.highScores === 'object' ? candidate.highScores : {};
    const stampSource = candidate.stamps && typeof candidate.stamps === 'object' ? candidate.stamps : {};
    GAME_IDS.forEach((id) => {
      const count = countSource[id];
      const score = scoreSource[id];
      const earned = stampSource[id];
      if (typeof count === 'number' && Number.isFinite(count) && count >= 0) playCounts[id] = Math.floor(count);
      if (typeof score === 'number' && Number.isFinite(score) && score >= 0) highScores[id] = score;
      if (Array.isArray(earned)) stamps[id] = [...new Set(earned.filter((stamp): stamp is StampKind => STAMP_IDS.includes(stamp as StampKind)))];
    });
    const settings: Partial<ArcadeSave['settings']> = candidate.settings && typeof candidate.settings === 'object' ? candidate.settings : {};
    const lastPlayed = GAME_IDS.includes(candidate.lastPlayed as GameId) ? candidate.lastPlayed as GameId : null;
    return {
      version: 1,
      lastPlayed,
      playCounts,
      highScores,
      stamps,
      settings: {
        muted: typeof settings.muted === 'boolean' ? settings.muted : false,
        reducedMotion: typeof settings.reducedMotion === 'boolean' ? settings.reducedMotion : freshSave().settings.reducedMotion,
      },
    };
  } catch {
    return freshSave();
  }
}

export function storeSave(save: ArcadeSave) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
  } catch {
    // Private browsing and full storage must not make the arcade unplayable.
  }
}

export function beginGame(save: ArcadeSave, id: GameId): ArcadeSave {
  const next = {
    ...save,
    lastPlayed: id,
    playCounts: { ...save.playCounts, [id]: (save.playCounts[id] ?? 0) + 1 },
  };
  storeSave(next);
  return next;
}

export function recordResult(save: ArcadeSave, id: GameId, score: number, stamps: StampKind[] = []): ArcadeSave {
  const earned = new Set([...(save.stamps[id] ?? []), ...stamps]);
  const next = {
    ...save,
    highScores: { ...save.highScores, [id]: Math.max(score, save.highScores[id] ?? 0) },
    stamps: { ...save.stamps, [id]: [...earned] },
  };
  storeSave(next);
  return next;
}
